import { Modal, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { useRouter } from 'expo-router';
import { X } from 'lucide-react-native';

const FRESHNESS_LABELS: Record<string, string> = {
  green: 'Very Fresh',
  blue: 'Fresh',
  amber: 'Stale',
  red: 'Very Stale',
};

const FRESHNESS_COLORS: Record<string, string> = {
  green: '#22c55e',
  blue: '#3b82f6',
  amber: '#f59e0b',
  red: '#ef4444',
};

type Props = {
  visible: boolean;
  onClose: () => void;
  userId: string;
  displayName: string | null;
  handle: string | null;
  strengthScore: number | null;
  freshnessBand: string | null;
  rank: number | null;
  wins: number | null;
  losses: number | null;
};

export default function LeaderboardMiniProfile({
  visible,
  onClose,
  userId,
  displayName,
  handle,
  strengthScore,
  freshnessBand,
  rank,
  wins,
  losses,
}: Props) {
  const { theme } = useTheme();
  const router = useRouter();
  const initial = (displayName ?? handle ?? '?')[0].toUpperCase();
  const freshLabel = FRESHNESS_LABELS[freshnessBand ?? ''] ?? 'Unknown';
  const freshColor = FRESHNESS_COLORS[freshnessBand ?? ''] ?? theme.colors.textSecondary;
  const w = wins ?? 0;
  const l = losses ?? 0;

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <TouchableOpacity style={styles.backdrop} activeOpacity={1} onPress={onClose}>
        <View style={[styles.card, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]} onStartShouldSetResponder={() => true}>
          <TouchableOpacity style={styles.closeBtn} onPress={onClose}>
            <X size={20} color={theme.colors.textSecondary} />
          </TouchableOpacity>

          {/* Avatar */}
          <View style={[styles.avatar, { backgroundColor: theme.colors.border }]}>
            <Text style={[styles.avatarText, { color: theme.colors.text }]}>{initial}</Text>
          </View>

          <Text style={[styles.name, { color: theme.colors.text }]}>{displayName ?? 'Unknown'}</Text>
          {handle && <Text style={[styles.handle, { color: theme.colors.textSecondary }]}>@{handle}</Text>}

          <View style={styles.statsRow}>
            <View style={styles.stat}>
              <Text style={[styles.statValue, { color: theme.colors.text }]}>#{rank ?? '—'}</Text>
              <Text style={[styles.statLabel, { color: theme.colors.textSecondary }]}>Rank</Text>
            </View>
            <View style={styles.stat}>
              <Text style={[styles.statValue, { color: theme.colors.text }]}>
                {strengthScore != null ? Math.round(strengthScore) : '—'}
              </Text>
              <Text style={[styles.statLabel, { color: theme.colors.textSecondary }]}>STR</Text>
            </View>
            <View style={styles.stat}>
              <Text style={[styles.statValue, { color: theme.colors.text }]}>{w}-{l}</Text>
              <Text style={[styles.statLabel, { color: theme.colors.textSecondary }]}>W-L</Text>
            </View>
            <View style={styles.stat}>
              <View style={[styles.freshDot, { backgroundColor: freshColor }]} />
              <Text style={[styles.statLabel, { color: freshColor }]}>{freshLabel}</Text>
            </View>
          </View>

          <View style={styles.actions}>
            {handle && (
              <TouchableOpacity
                style={[styles.btn, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border, borderWidth: 1 }]}
                onPress={() => {
                  onClose();
                  router.push({ pathname: '/u/[handle]' as any, params: { handle } });
                }}
              >
                <Text style={[styles.btnText, { color: theme.colors.text }]}>View Profile</Text>
              </TouchableOpacity>
            )}
            <TouchableOpacity
              style={[styles.btn, { backgroundColor: theme.colors.accent }]}
              onPress={() => {
                onClose();
                router.push({ pathname: '/(tabs)/duels' as any, params: { challengeUserId: userId } });
              }}
            >
              <Text style={[styles.btnText, { color: '#fff' }]}>Challenge</Text>
            </TouchableOpacity>
          </View>
        </View>
      </TouchableOpacity>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  card: {
    width: '100%',
    borderRadius: 4,
    borderWidth: 1,
    padding: 24,
    alignItems: 'center',
  },
  closeBtn: { position: 'absolute', top: 12, right: 12, padding: 4 },
  avatar: {
    width: 56,
    height: 56,
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 10,
  },
  avatarText: { fontSize: 24, fontWeight: '700' },
  name: { fontSize: 18, fontWeight: '800' },
  handle: { fontSize: 13, fontWeight: '500', marginTop: 2 },
  statsRow: {
    flexDirection: 'row',
    gap: 20,
    marginTop: 16,
    marginBottom: 20,
  },
  stat: { alignItems: 'center', gap: 2 },
  statValue: { fontSize: 18, fontWeight: '800' },
  statLabel: { fontSize: 11, fontWeight: '600' },
  freshDot: { width: 10, height: 10, borderRadius: 4, marginTop: 4 },
  actions: { flexDirection: 'row', gap: 10, width: '100%' },
  btn: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 2,
    alignItems: 'center',
  },
  btnText: { fontSize: 14, fontWeight: '700' },
});

